import React, { useState } from 'react';
import { Target, Plus, Trophy, Calendar, Trash2, Edit2, Zap, Award, Sparkles, CheckCircle2, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import ChallengeCelebrationModal from './ChallengeCelebrationModal';

const GoalsWidget = ({ goalsData = [], onOpenCreator, onDeleteGoal }) => {
    const [activeTab, setActiveTab] = useState('active'); // 'active' | 'completed'
    const [celebratingChallenge, setCelebratingChallenge] = useState(null);

    const calculateProjection = (goal) => {
        const progress = goal.progress || 0;
        const target = goal.target || 0;
        if (!target) return null;

        const now = new Date();
        
        if (goal.timeframe === 'annual') {
            const startOfYear = new Date(now.getFullYear(), 0, 1);
            const dayOfYear = Math.max(1, Math.floor((now - startOfYear) / (1000 * 60 * 60 * 24)) + 1);
            const isLeapYear = (year) => (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
            const totalDays = isLeapYear(now.getFullYear()) ? 366 : 365;
            
            const projectedVal = (progress / dayOfYear) * totalDays;
            const diff = projectedVal - target;

            return {
                projectedVal,
                diff,
                label: 'Proyección fin de año'
            };
        }

        if (goal.timeframe === 'monthly') {
            const dayOfMonth = now.getDate();
            const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            
            const projectedVal = (progress / dayOfMonth) * daysInMonth;
            const diff = projectedVal - target;

            return {
                projectedVal,
                diff,
                label: 'Proyección fin de mes'
            };
        }

        if (goal.timeframe === 'weekly') {
            let dayOfWeek = now.getDay();
            if (dayOfWeek === 0) dayOfWeek = 7;
            
            const projectedVal = (progress / dayOfWeek) * 7;
            const diff = projectedVal - target;

            return {
                projectedVal,
                diff,
                label: 'Proyección fin de semana'
            };
        }

        return null;
    };

    const getSportIcon = (sport) => {
        const s = (sport || '').toLowerCase();
        if (s.includes('ciclismo') || s.includes('cycling')) return '🚴';
        if (s.includes('running') || s.includes('correr')) return '🏃';
        if (s.includes('natación') || s.includes('swim')) return '🏊';
        if (s.includes('fuerza') || s.includes('weight')) return '💪';
        if (s.includes('caminar') || s.includes('walk')) return '🚶';
        if (s.includes('pádel') || s.includes('tenis')) return '🎾';
        if (s.includes('crossfit')) return '🏋️';
        if (s.includes('pilates') || s.includes('yoga')) return '🧘';
        if (s.includes('fútbol')) return '⚽';
        return '🎯';
    };

    const formatTimeframe = (tf) => {
        if (tf === 'weekly') return 'Semanal';
        if (tf === 'monthly') return 'Mensual';
        if (tf === 'annual') return 'Anual';
        return tf || 'Personalizado';
    };

    const formatMetricUnit = (metric, unit) => {
        if (metric === 'distance') return 'km';
        if (metric === 'sessions') return 'salidas';
        if (metric === 'elevation') return 'm D+';
        if (metric === 'duration') return 'h';
        return unit || '';
    };

    // Filtrar retos activos y completados
    const activeGoals = (goalsData || []).filter(g => {
        const pct = ((g.progress || 0) / g.target) * 100;
        return pct < 100;
    });

    const completedGoals = (goalsData || []).filter(g => {
        const pct = ((g.progress || 0) / g.target) * 100;
        return pct >= 100;
    });

    const displayedGoals = activeTab === 'active' ? activeGoals : completedGoals;

    // Componente Anillo de Progreso SVG
    const ProgressRing = ({ percentage = 0, size = 64, strokeWidth = 6, isCompleted = false }) => {
        const radius = (size - strokeWidth) / 2;
        const circumference = radius * 2 * Math.PI;
        const strokeDashoffset = circumference - (Math.min(100, percentage) / 100) * circumference;
        const strokeColor = isCompleted ? '#10b981' : percentage > 66 ? '#10b981' : percentage > 33 ? '#3b82f6' : '#f59e0b';

        return (
            <div style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke="rgba(255, 255, 255, 0.1)"
                        strokeWidth={strokeWidth}
                        fill="transparent"
                    />
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke={strokeColor}
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        fill="transparent"
                        style={{ transition: 'stroke-dashoffset 0.8s ease-in-out' }}
                    />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    {isCompleted ? (
                        <CheckCircle2 size={20} color="#10b981" />
                    ) : (
                        <span style={{ fontSize: '0.8rem', fontWeight: 800, color: strokeColor }}>
                            {Math.round(percentage)}%
                        </span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div style={{
            position: 'relative',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.7))',
            padding: '1.75rem',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.4)',
            overflow: 'hidden'
        }}>
            {/* Modal de Celebración si se activa */}
            {celebratingChallenge && (
                <ChallengeCelebrationModal
                    challenge={celebratingChallenge}
                    onClose={() => setCelebratingChallenge(null)}
                />
            )}

            {/* HEADER BENTO BOX */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ padding: '10px', borderRadius: '14px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Trophy size={22} />
                    </div>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            Retos y Objetivos Deportivos
                        </h3>
                        <span style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                            Desafíos semanales y mensuales para potenciar tu entrenamiento
                        </span>
                    </div>
                </div>

                {/* BOTONES DE ACCIÓN */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button 
                        onClick={() => window.dispatchEvent(new CustomEvent('force-sync-activities'))} 
                        title="Sincronizar progreso"
                        style={{
                            background: 'rgba(255, 255, 255, 0.05)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)', padding: '0.5rem 0.75rem',
                            borderRadius: '10px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '4px'
                        }}
                    >
                        <RefreshCw size={14} /> Sincronizar
                    </button>
                    <button 
                        onClick={() => onOpenCreator(null)} 
                        style={{
                            background: 'linear-gradient(135deg, #10b981, #059669)', color: '#ffffff', border: 'none', padding: '0.5rem 0.9rem',
                            borderRadius: '10px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px',
                            boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)'
                        }}
                    >
                        <Plus size={16} /> Nuevo Reto
                    </button>
                </div>
            </div>

            {/* PESTAÑAS (ACTIVOS vs COMPLETADOS) */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '0.75rem' }}>
                <button
                    onClick={() => setActiveTab('active')}
                    style={{
                        background: activeTab === 'active' ? 'rgba(16, 185, 129, 0.15)' : 'transparent',
                        color: activeTab === 'active' ? '#10b981' : 'rgba(255, 255, 255, 0.6)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.4rem 0.85rem',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s'
                    }}
                >
                    <Target size={15} /> Retos Activos ({activeGoals.length})
                </button>
                <button
                    onClick={() => setActiveTab('completed')}
                    style={{
                        background: activeTab === 'completed' ? 'rgba(245, 158, 11, 0.15)' : 'transparent',
                        color: activeTab === 'completed' ? '#f59e0b' : 'rgba(255, 255, 255, 0.6)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.4rem 0.85rem',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s'
                    }}
                >
                    <Award size={15} /> Logros y Trofeos ({completedGoals.length})
                </button>
            </div>


            {/* CONTENIDO PRINCIPAL: LISTADO BENTO BOX */}
            {displayedGoals.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '2.5rem 1rem',
                    background: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: '16px',
                    border: '1px dashed rgba(255, 255, 255, 0.08)'
                }}>
                    <Trophy size={36} color="rgba(255,255,255,0.3)" style={{ marginBottom: '0.75rem' }} />
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: '#ffffff' }}>
                        {activeTab === 'active' ? 'No tienes retos activos en este momento' : 'Aún no has completado ningún reto'}
                    </h4>
                    <p style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.5)', margin: '0 0 1.25rem 0' }}>
                        {activeTab === 'active' ? '¡Activa un reto rápido como 100 km al mes o 5 salidas en bici!' : '¡Supera tu primer reto para desbloquear insignias de logro!'}
                    </p>
                    <button
                        onClick={() => onOpenCreator(null)}
                        style={{
                            background: 'rgba(16, 185, 129, 0.12)',
                            color: '#10b981',
                            border: '1px solid rgba(16, 185, 129, 0.25)',
                            padding: '0.5rem 1rem',
                            borderRadius: '10px',
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            cursor: 'pointer'
                        }}
                    >
                        + Crear primer reto
                    </button>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: '1rem'
                }}>
                    {displayedGoals.map((goal) => {
                        const progress = goal.progress || 0;
                        const pct = Math.min(100, (progress / goal.target) * 100) || 0;
                        const isCompleted = pct >= 100;
                        const unitText = formatMetricUnit(goal.metric, goal.unit);
                        const projection = calculateProjection(goal);

                        return (
                            <div
                                key={goal.id}
                                style={{
                                    background: isCompleted ? 'rgba(16, 185, 129, 0.04)' : 'rgba(255, 255, 255, 0.03)',
                                    borderRadius: '18px',
                                    border: isCompleted ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid rgba(255, 255, 255, 0.07)',
                                    padding: '1.25rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justify: 'space-between',
                                    gap: '1rem',
                                    transition: 'all 0.25s ease',
                                    position: 'relative'
                                }}
                            >
                                {/* Crecimiento visual & Cabecera de Tarjeta */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <ProgressRing percentage={pct} size={54} isCompleted={isCompleted} />
                                        <div>
                                            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span>{getSportIcon(goal.sport)}</span>
                                                <span>{goal.sport === 'All' || goal.sport === 'Cualquiera' ? 'Global' : goal.sport}</span>
                                            </h4>
                                            <span style={{ fontSize: '0.75rem', color: isCompleted ? '#10b981' : 'var(--health-cyan)', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '3px' }}>
                                                <Calendar size={12} /> {formatTimeframe(goal.timeframe)}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <button 
                                            onClick={() => onOpenCreator(goal)}
                                            style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: '4px' }}
                                            title="Editar"
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                        <button 
                                            onClick={() => onDeleteGoal(goal.id)}
                                            style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.25)', cursor: 'pointer', padding: '4px' }}
                                            title="Eliminar"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>

                                {/* METRICAS Y PROGRESO */}
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '6px' }}>
                                        <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                                            {progress.toLocaleString()} / {goal.target.toLocaleString()} {unitText}
                                        </span>
                                        <span style={{ fontWeight: 800, color: isCompleted ? '#10b981' : '#3b82f6' }}>
                                            {pct.toFixed(0)}%
                                        </span>
                                    </div>

                                    {/* Barra horizontal sutil */}
                                    <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '4px', overflow: 'hidden' }}>
                                        <div style={{
                                            width: `${pct}%`,
                                            height: '100%',
                                            background: isCompleted ? '#10b981' : 'linear-gradient(90deg, #3b82f6, #10b981)',
                                            borderRadius: '4px',
                                            transition: 'width 0.6s ease-in-out'
                                        }} />
                                    </div>

                                    {/* ESTIMACIÓN / PROYECCIÓN AL RITMO ACTUAL */}
                                    {projection && !isCompleted && (
                                        <div style={{
                                            marginTop: '10px',
                                            padding: '6px 10px',
                                            borderRadius: '8px',
                                            background: projection.diff >= 0 ? 'rgba(16, 185, 129, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                                            border: projection.diff >= 0 ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid rgba(245, 158, 11, 0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            fontSize: '0.73rem'
                                        }}>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.65)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {projection.diff >= 0 ? (
                                                    <TrendingUp size={13} color="#10b981" />
                                                ) : (
                                                    <TrendingDown size={13} color="#f59e0b" />
                                                )}
                                                {projection.label}:
                                            </span>
                                            <span style={{ fontWeight: 700, color: projection.diff >= 0 ? '#10b981' : '#f59e0b' }}>
                                                {projection.projectedVal.toLocaleString('es-ES', { maximumFractionDigits: 1 })} {unitText}
                                                {projection.diff > 0 ? ` (+${projection.diff.toLocaleString('es-ES', { maximumFractionDigits: 1 })})` : ''}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* BOTON DE CELEBRACION SI ESTA COMPLETADO */}
                                {isCompleted && (
                                    <button
                                        onClick={() => setCelebratingChallenge(goal)}
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem',
                                            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(245, 158, 11, 0.05))',
                                            border: '1px solid rgba(245, 158, 11, 0.4)',
                                            borderRadius: '10px',
                                            color: '#f59e0b',
                                            fontSize: '0.8rem',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <Sparkles size={14} /> Ver Celebración de Logro
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default GoalsWidget;
